<template>
  <section
    class="bg-gradient-to-r from-gray-100/70 to-gray-100 relative py-8 sm:py-12"
  >
    <div class="container relative">
      <h1 class="text-3xl/tight font-medium mb-4"></h1>
      <Wizard
        squared-tabs
        card-background
        scrollable-tabs
        :doneButton="{
          text: 'Tamamla',
          icon: 'file',
          disabled: true,
        }"
        :nextButton="nextButtonOptions"
        :backButton="backButton"
        :custom-tabs="[
          {
            title: 'Paket Seçimi',
          },
          {
            title: 'Ek Özellikler',
          },
          {
            title: 'Fatura Bilgileri',
          },
          {
            title: 'Ödeme',
          },
        ]"
        @change="onChangeCurrentTab"
      >
        <h2 v-if="currentTabIndex === 0">Paket Seçimi</h2>
        <h5 v-if="currentTabIndex === 1">Ek Özellikler</h5>
        <h5 v-if="currentTabIndex === 2">Fatura Bilgileri</h5>
        <h5 v-if="currentTabIndex === 3">Ödeme</h5>

        <div v-if="currentTabIndex === 0">
          <step-one
            @on-yearly-change="onYearlyChange"
            @on-selected-package-change="onSelectedPackageChange"
          ></step-one>
        </div>

        <div v-if="currentTabIndex === 1">
          <step-two
            @handle-quantity-change="handleQuantityChange"
            :is-yearly="isYearly"
            :quantities="quantities"
          ></step-two>
        </div>

        <div v-if="currentTabIndex === 2">
          <step-three
            @handle-invoice-information-change="handleInvoiceInformationChange"
          ></step-three>
        </div>

        <div v-if="currentTabIndex === 3">
          <div v-if="createdOrder == null">
            <div class="text-center">
              <i class="fas fa-spinner fa-spin text-4xl text-primary"></i>
            </div>
          </div>
          <step-four
            :createdOrder="createdOrder"
            :is-yearly="isYearly"
            v-else
          ></step-four>
        </div>
      </Wizard>
    </div>
  </section>
</template>

<script setup>
import { onMounted, ref, computed } from "vue";
import Wizard from "form-wizard-vue3";
import "form-wizard-vue3/dist/form-wizard-vue3.css";
import StepOne from "@/components/DashboardLayout/WizardComponents/StepOne.vue";
import StepTwo from "@/components/DashboardLayout/WizardComponents/StepTwo.vue";
import StepThree from "@/components/DashboardLayout/WizardComponents/StepThree.vue";
import StepFour from "@/components/DashboardLayout/WizardComponents/StepFour.vue";
import { Toast } from "@/services/toastService";
import { usePackageStore } from "@/stores/packageStore";
import createOrderDTO from "@/models/Order/CreateOrderDTO";
import OrderItemsDTO from "@/models/Order/OrderItemsDTO";

import { createOrder } from "@/services/userServices";

const packageStore = usePackageStore();

// refs
const currentTabIndex = ref(0);
const createdOrder = ref(null);
const isYearly = ref(true);
const selectedPackage = ref(null);
const quantities = ref({});
const selectedInvoiceInformation = ref(null);

const backButton = computed(() => {
  return {
    text: "Geri Dön",
    icon: "arrow-left",
    hideText: false,
    hideIcon: false,
    disabled: false,
  };
});

const nextButtonOptions = computed(() => {
  if (currentTabIndex.value === 0) {
    if (selectedPackage.value) {
      return {
        text: "Devam",
        icon: "check",
        hideIcon: true, // default false but selected for sample
        hideText: false, // default false but selected for sample
        disabled: false,
      };
    } else {
      return {
        text: "Paket Seçiniz",
        icon: "check",
        hideIcon: true, // default false but selected for sample
        hideText: false, // default false but selected for sample
        disabled: true,
      };
    }
  }

  if (currentTabIndex.value === 2) {
    if (selectedInvoiceInformation.value == null) {
      return {
        text: "Fatura Bilgisi Seçiniz",
        icon: "check",
        hideIcon: true, // default false but selected for sample
        hideText: false, // default false but selected for sample
        disabled: true,
      };
    } else {
      return {
        text: "Devam",
        icon: "check",
        hideIcon: true, // default false but selected for sample
        hideText: false, // default false but selected for sample
        disabled: false,
      };
    }
  }

  return {
    text: "Devam",
    icon: "check",
    hideIcon: true, // default false but selected for sample
    hideText: false, // default false but selected for sample
    disabled: false,
  };
});

const onChangeCurrentTab = (currentTab) => {
  if (currentTab === null) {
    currentTabIndex.value = 0;
  } else {
    currentTabIndex.value = currentTab;
  }

  if (currentTab === 3) {
    // create order
    var orderItems = packageStore.extraFeatures
      .filter((feature) => quantities.value[feature.Id] > 0)
      .map((feature) => {
        return new OrderItemsDTO(
          null,
          isYearly.value,
          feature.Id,
          quantities.value[feature.Id]
        );
      });

    // add package to order
    orderItems.push(
      new OrderItemsDTO(selectedPackage.value.Id, isYearly.value, null, 1)
    );

    const order = new createOrderDTO(
      "TL",
      selectedInvoiceInformation.value.Id,
      orderItems
    );

    createOrder(order).then((response) => {
      createdOrder.value = response.Payload;

      if (response.Success) {
        Toast("Başarılı", "Siparişiniz başarıyla oluşturuldu.", "success");
      } else {
        Toast("Hata", response.Message, "error");
      }
    });
  }
};

// --------------------------------------------

// Step 1 props and methods

const onYearlyChange = (isYearlyResult) => {
  isYearly.value = isYearlyResult;
};
const onSelectedPackageChange = (packagex) => {
  selectedPackage.value = packagex;
};

// Step 2 props and methods

const handleQuantityChange = (quantities) => {
  quantities.value = quantities;
};

// Step 3 props and methods

const handleInvoiceInformationChange = (invoiceInformation) => {
  selectedInvoiceInformation.value = invoiceInformation;
};

// Step 4 props and methods

onMounted(async () => {
  if (packageStore.packages.length === 0) {
    packageStore.fetchPackages();
  }
  currentTabIndex.value = 0;
  if (packageStore.extraFeatures.length === 0) {
    await packageStore.fetchExtraFeatures();
  }

  quantities.value = packageStore.extraFeatures.reduce((acc, feature) => {
    acc[feature.Id] = 0;
    return acc;
  }, {});
});
</script>
